import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { DatePickerStateOptions, useDatePickerState } from 'react-stately';
import { DateValue, useDatePicker } from '@react-aria/datepicker';
import classNames from 'classnames';

import { CalendarIcon } from '../../../../assets';
import { Calendar, CalendarPopover, Dialog, FieldButton } from '../';
import { DateField } from '../DateField';

import styles from './DatePicker.module.scss';

export interface IDatePickerProps extends DatePickerStateOptions<DateValue> {
    popoverId?: string;
    error?: boolean;
    placeholder?: string;
    size?: 'small' | 'regular';
}

export const DatePicker = forwardRef<HTMLDivElement, IDatePickerProps>(
    ({ popoverId, error, placeholder, size = 'regular', ...props }, ref) => {
        const localRef = useRef<HTMLDivElement>(null);
        useImperativeHandle(ref, () => localRef.current!, []);

        const state = useDatePickerState(props);
        const { isOpen } = state;
        const { groupProps, fieldProps, dialogProps, buttonProps, calendarProps } = useDatePicker(
            props,
            state,
            localRef,
        );

        return (
            <div
                ref={localRef}
                {...groupProps}
                className={classNames(
                    styles['date-picker'],
                    styles[`size-${size}`],
                    {
                        [styles.error]: error,
                        [styles['date-picker-open']]: isOpen,
                    },
                )}
            >
                <DateField
                    compact={size === 'small'}
                    id={popoverId}
                    placeholder={placeholder}
                    {...fieldProps}
                />
                <FieldButton isDisabled={props.isDisabled} {...buttonProps}>
                    <CalendarIcon />
                </FieldButton>
                {isOpen && (
                    <CalendarPopover triggerRef={localRef} state={state}>
                        <Dialog {...dialogProps}>
                            <Calendar {...calendarProps} />
                        </Dialog>
                    </CalendarPopover>
                )}
            </div>
        );
    },
);
